import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import moment from "moment";

import {
  getLeaseEvents,
  getLeaseEventsIgnored,
  getLeaseEventsSnoozed,
  snoozeLeaseEvents,
  ignoreLeaseEvents,
  unSnoozeLeaseEvents,
  unIgnoreLeaseEvents,
  getLeaseEventFilterOptions,
  getLeaseEventPageSize,
  getLeaseEventSpreadsheet,
} from "redux/actions/leaseEventActions";
import {
  getColumnSettings,
  setColumnVisibility,
  setColumnOrder,
  getFilterSettings,
  setFilterSettings,
  updatePageSizeSetting,
  getUserViewSettings,
  setViewAsDefault,
  saveUserViewSetting,
  updateUserViewSetting,
  deleteUserViewSetting,
  getNoticeDate
} from "redux/actions/userSettingsActions";

import {
  clearTransactionDetails
} from "redux/actions/transactionActions";
import { getPropertyTypes } from "../../../redux/actions/propertyActions";

import Paper from "@mui/material/Paper";

import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";

import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";

import Button from "@mui/material/Button";

import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";

import { LoadingButton } from "@mui/lab";

import SearchIcon from "@mui/icons-material/Search";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ListIcon from "@mui/icons-material/List";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as AddViewIcon } from "assets/icons/AddViewIcon.svg";

import { FitWidth, Row } from "components/common";

import CustomTabs, { Tab as CustomTab } from "components/Tabs";

import TableHeader from "components/TableHeader";

import ViewListDrawer from "components/SolsticeComponents/ViewListDrawer";
import CreateViewModal from "components/SolsticeComponents/CreateViewModal";
import ViewActionModal from "components/SolsticeComponents/ViewActionModal";
import SuccessAlert from "components/SolsticeComponents/SuccessAlert";
import CustomTooltip from "components/CustomTooltip/CustomTooltip";
import NotificationAlert from "components/Notification/NotificationAlert";
import ActionButtons from "./components/ActionButtons";
import FilterModal from "./components/FilterModal";
import ColumnVisibilityModal from "./components/ColumnVisibilityModal";

import LeaseEventsTable from "./LeaseEventsTable";
import DetailsModal from "./DetailsModal";
import SnoozeIgnoreModal from "./SnoozeIgnoreModal";

import Alert from "components/Alert";

import { Container } from "./styles";
import "./styles.css";
import { useHistory, useLocation } from "react-router-dom";
import { getRegions } from "redux/actions/referenceActions";
import {
  LeaseEvent,
  LeaseEventNotificationComment
} from "../../../shared/utils/constants";
import * as userActions from "redux/actions/userActions";

function usePrevious(value) {
  const ref = useRef();
  
  useEffect(() => {
    ref.current = value;
  }, [value]);
  
  return ref.current;
}

const selectableEventTypes = [
  { name: "all", label: "All lease events" },
  { name: "snoozed", label: "Snoozed lease events" },
  { name: "ignored", label: "Ignored lease events" },
];

const noticeDateRanges = [
  {
    value: 0,
    label: "All",
  },
  {
    value: 3,
    label: "Within 3 months",
  },
  {
    value: 6,
    label: "Within 6 months",
  },
  {
    value: 12,
    label: "Within 12 months",
  },
  {
    value: 18,
    label: "Within 18 months",
  },
  {
    value: 24,
    label: "Within 24 months",
  },
  {
    value: 36,
    label: "Within 36 months",
  },
];

const defaultColumnVisibility = {
  leaseEventId: false,
  eventId: true,
  leaseCode: true,
  leaseName: true,
  propertyName: true,
  streetAddress1: true,
  businessUnit: true,
  countryName: true,
  stateProvince: true,
  city: true,
  rentableBuildingArea: true,
  eventType: true,
  leaseEventStatusName: true,
  eventNote: false,
  lastComment: true,
  lastCommentCreatedDate: true,
  tenureType: true,
  noticeDate: true,
  actionDate: true,
  expireDate: true,
  transactions: true,
};

const nonHideableColumns = ["leaseName", "tenureType"];

const LeaseEvents = (props) => {
  const dispatch = useDispatch();
  const { parent = "leaseEventsPage", actions } = props;
  const history = useHistory();
  const location = useLocation();
  const selectedClientId = useSelector(
    (state) => state.clients.selectedClientId
  );
  const allLeaseEvents = useSelector((state) => state.leaseEvents.upcoming);
  const savedFilterSettings = useSelector(
    (state) => state.userSettings.filterSettings
  );
  const columnSettings = useSelector(
    (state) => state.userSettings.columnSettings
  );
  const userViewSettings = useSelector(
    (state) => state.userSettings.userViewSettings
  );
  const leaseEventPageSize = useSelector(
    (state) => state.leaseEvents.leaseEventPageSize
  );
  const leaseEventsIgnored = useSelector((state) => state.leaseEvents.ignored);
  const leaseEventsSnoozed = useSelector((state) => state.leaseEvents.snoozed);
  const regions = useSelector((state) => state.references.regions);
  const leaseNames = useSelector((state) => state.leaseEvents.leaseNames);
  const cities = useSelector((state) => state.leaseEvents.cities);
  const stateProvinces = useSelector(
    (state) => state.leaseEvents.stateProvinces
  );
  const countries = useSelector((state) => state.leaseEvents.countries);
  const leaseTypes = useSelector((state) => state.leaseEvents.leaseTypes);
  const leaseStatuses = useSelector((state) => state.leaseEvents.leaseStatuses);
  const eventTypes = useSelector((state) => state.leaseEvents.eventTypes);
  const eventStatuses = useSelector((state) => state.leaseEvents.eventStatuses);
  const tenureTypes = useSelector((state) => state.leaseEvents.tenureTypes);
  const propertyTypes = useSelector((state) => state.leaseEvents.propertyTypes);
  const businessUnits = useSelector((state) => state.leaseEvents.businessUnits);
  const user = useSelector((state) => state.currentUser.details);
  const userId = useSelector((state) => state.currentUser.details.id);
  const selectedNoticeDate = useSelector((state) => state.userSettings.noticeDate);
  const { userEmailAddress } = user;

  const savedFilterTableName = "LEASE_EVENTS_LIST_ALL";

  const [loading, setLoading] = useState(false);
  const [loadingFilterOptions, setLoadingFilterOptions] = useState(false);

  const [selectedType, setSelectedType] = useState("all");
  const [alertMessage, setAlertMessage] = useState("");
  const [alertError, setAlertError] = useState("");
  const [notificationAlert, setNotificationAlert] = useState({
    message: "",
    severity: "success",
    title: "",
    open: false,
  });
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [columnModalOpen, setColumnModalOpen] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [snoozeIgnoreModalOpen, setSnoozeIgnoreModalOpen] = useState(false);
  const [snoozeIgnoreAction, setSnoozeIgnoreAction] = useState("");
  const [selectedNoticeDateRange, setSelectedNoticeDateRange] = useState(
    parent === "leaseEventsPage" ? 0 : 18
  );
  const [searchKeyword, setSearchKeyword] = useState("");
  const [isLoadedOnce, setLoadedOnce] = useState(false);
  const [isInitialFilterLoad, setIsInitialFilterLoad] = useState(false);
  const [columnVisibilityByType, setcolumnVisibilityByType] = useState({});
  const [columnVisibilityModel, setcolumnVisibilityModel] = useState({
    leaseEvents: {},
    leaseEventsSnoozed: {},
    leaseEventsIgnored: {},
  });

  const [filters, setFilters] = useState([
    {
      name: "names",
      column: "leaseName",
      label: "Lease Name",
      options: [],
    },
    {
      name: "regionIds",
      column: "region",
      label: "Region",
      options: [],
      idMap: [],
    },
    {
      name: "countries",
      column: "country",
      label: "Country",
      options: [],
    },
    {
      name: "stateProvinces",
      column: "state",
      label: "State / Province",
      options: [],
    },
    {
      name: "cities",
      column: "city",
      label: "City",
      options: [],
    },
    {
      name: "leaseTypes",
      column: "leaseType",
      label: "Lease Type",
      options: [],
    },
    {
      name: "leaseStatuses",
      column: "leaseStatus",
      label: "Lease Status",
      options: [],
    },
    {
      name: "eventTypes",
      column: "eventType",
      label: "Event Type",
      options: [],
    },
    {
      name: "eventStatuses",
      column: "leaseEventStatusName",
      label: "Event Status",
      options: [],
    },
    {
      name: "tenureTypes",
      column: "tenureType",
      label: "Tenure Type",
      options: [],
    },
    {
      name: "propertyTypes",
      column: "propertyType",
      label: "Property Type",
      options: [],
    },
    {
      name: "businessUnits",
      column: "businessUnit",
      label: "Business Unit",
      options: [],
    },
    {
      name: "noticeDateRange",
      column: "noticeDate",
      label: "Notice Date",
      options: [],
    },
  ]);
  const selectedNotification = useSelector(
    (state) => state.currentUser.selectedNotification
  );
  const disableOtherFilter = selectedNotification && (selectedNotification.type === LeaseEvent || selectedNotification.type === LeaseEventNotificationComment) ? true : false;
  const noticeDate = selectedNotification && selectedNotification.type === LeaseEvent ? selectedNotification.typeEntityDueDate : null;
  const selectedNotificationComment = selectedNotification && selectedNotification.type === LeaseEventNotificationComment ? selectedNotification.notificationMessage : null;
  const selectedNotificationCommentId =  selectedNotification && selectedNotification.type === LeaseEventNotificationComment ? selectedNotification.entityCommentId : null; 

  
  const [selectedFilters, setSelectedFilters] = useState({});

  const [currentPage, setCurrentPage] = useState({
    leaseEvents: 1,
    leaseEventsSnoozed: 1,
    leaseEventsIgnored: 1,
  });

  const [currentSort, setCurrentSort] = useState({
    leaseEvents: {
      sortColumn: "noticeDate",
      sortDirection: "ASC",
    },
    leaseEventsSnoozed: {
      sortColumn: "",
      sortDirection: "",
    },
    leaseEventsIgnored: {
      sortColumn: "",
      sortDirection: "",
    },
  });

  const [selectedIds, setSelectedIds] = useState([]);

  const [isPageSizeUpdated, setIsPageSizeUpdated] = useState(false); 

  const [detailsSettings, setDetailsSettings] = useState({
    open: false,
    selectedLeaseEventId: 0,
  });

  /* View state (leaseEventsPage only) */
  const [viewDrawerOpen, setViewDrawerOpen] = useState(false);
  const [appliedViewId, setAppliedViewId] = useState(null);
  const [lastAddedViewName, setLastAddedViewName] = useState("");
  const [addViewEnabled, setAddViewEnabled] = useState(false);
  const [updateViewEnabled, setUpdateViewEnabled] = useState(false);
  const [viewToDelete, setViewToDelete] = useState(null);
  const [viewToShare, setViewToShare] = useState(null);
  const [shareRestricted, setShareRestricted] = useState(false);
  const [viewToMakePrivate, setViewToMakePrivate] = useState(null);
  const [viewToRestrict, setViewToRestrict] = useState(null);
  const [viewToUnrestrict, setViewToUnrestrict] = useState(null);
  const [addViewDialogOpen, setAddViewDialogOpen] = useState(false);
  const [columnOrderSettings, setColumnOrderSettings] = useState([]);
  const [successAlertOpen, setSuccessAlertOpen] = useState(false);
  const [successAlertMessage, setSuccessAlertMessage] = useState("");
  const [loadedViewFilters, setLoadedViewFilters] = useState(null);
  const [loadedViewColumns, setLoadedViewColumns] = useState(null);
  const userFiltersLoaded = useRef(false);
  const [triggerLeasesLoad, setTriggerLeasesLoad] = useState(0);
  const prevTriggerLeasesLoadRef = useRef(0);
  const hasInitialViewLoad = useRef(false);
  const isLoadingViewSettings = useRef(false);
  const lastLoadedViewId = useRef(null);
  const selectedViewIdRef = useRef(null);
  const isViewTransitioning = useRef(false);
  const initialLoadComplete = useRef(false);
  const prevClientIdRef = useRef(null);
  const lastLocationPath = useRef(null);
  const prevRequestRef = useRef(null);

  const views = useMemo(
    () => (Array.isArray(userViewSettings) ? userViewSettings : []),
    [userViewSettings]
  );
  const selectedViewId = useMemo(() => {
    if (appliedViewId) return appliedViewId;
    const defaultView = views.find((v) => v.isDefault);
    if (defaultView) return defaultView.id;
    return null;
  }, [appliedViewId, views]);
  const hasOwnedViews = useMemo(
    () => views.some((v) => v.isOwner === true),
    [views]
  );
  const canUpdateCurrentView = useMemo(() => {
    if (!selectedViewId) return false;
    const currentView = views.find((v) => v.id === selectedViewId);
    if (!currentView) return false;
    return currentView.isOwner === true || currentView.isRestricted === false;
  }, [selectedViewId, views]);

  // Notification-driven list uses notice-date-only filtering; show no view as selected in the UI
  // while keeping appliedViewId / state intact so the normal view flow returns when the notification clears.
  const viewDrawerSelectedId = disableOtherFilter ? null : selectedViewId;
  const headerViewDisplayName = disableOtherFilter
    ? "Unselected"
    : lastAddedViewName || "Unselected";

  const effectiveSelectedFilters = useMemo(
    () => (disableOtherFilter ? {} : selectedFilters || {}),
    [disableOtherFilter, selectedFilters]
  );

  const handleClearLeaseEventNotificationFilter = useCallback(() => {
    dispatch(userActions.resetSelectedNotificationComment());
  }, [dispatch]);

  useEffect(()=>{      
    if (userId && savedFilterTableName) {
      getNumberOfRows();
      dispatch(getNoticeDate(userId, savedFilterTableName));
    }
  },[userId, savedFilterTableName]);  

  useEffect(()=>{
    if (selectedNoticeDate?.value) {
      setSelectedNoticeDateRange(Number(selectedNoticeDate?.value));
    }
  },[selectedNoticeDate]);

  const getNumberOfRows = ()=>{
    setIsPageSizeUpdated(false);
    dispatch(getLeaseEventPageSize(userId, savedFilterTableName))
      .catch((err) => {
        setAlertError(`Error: ${err}`);
      })
      .finally(() => {
        setIsPageSizeUpdated(true);
      });
  }

  const buildRequest = () => {
    let request = {
      clientId: selectedClientId,
      searchKeyword,
      ...computeNoticeDates(selectedNoticeDateRange),
      filters: selectedFilters,
      pageNumber: 1,
      pageSize: leaseEventPageSize,
      commentId: selectedNotificationCommentId ? selectedNotificationCommentId : null,
    };

    switch (selectedType) {
      case "all":
      default:
        request.pageNumber = currentPage.leaseEvents;
        request.sortColumn = currentSort.leaseEvents.sortColumn;
        request.sortDirection = currentSort.leaseEvents.sortDirection;

        break;

      case "snoozed":
        request.pageNumber = currentPage.leaseEventsSnoozed;
        request.sortColumn = currentSort.leaseEventsSnoozed.sortColumn;
        request.sortDirection = currentSort.leaseEventsSnoozed.sortDirection;

        break;

      case "ignored":
        request.pageNumber = currentPage.leaseEventsIgnored;
        request.sortColumn = currentSort.leaseEventsIgnored.sortColumn;
        request.sortDirection = currentSort.leaseEventsIgnored.sortDirection;

        break;
    }

    return request;
  };

  const updatePageSize = (pageSize) => {    
    // setLoading(true);      
    dispatch(updatePageSizeSetting(userId, savedFilterTableName, pageSize, userEmailAddress))
      .catch((err) => {
        setAlertError(`Error: ${err}`);
      })
      .finally(() => {
        getNumberOfRows();
        // setLoading(false);
      });    
  };

  useEffect(() => {
    if (user && user.id) {
      const { id } = user;
      // if (parent === "leaseEventsPage" && selectedType === "all") {
      //   getUserSettings(selectedType);
      //   return;
      // }
      dispatch(getColumnSettings(savedFilterTableName, id));
      // switch (selectedType) {
      //   case "all":
      //   default:
      //     dispatch(getColumnSettings("LEASE_EVENTS_LIST", id));
      //     break;

      //   case "snoozed":
      //     dispatch(getColumnSettings("SNOOZED_LEASE_EVENTS_LIST", id));
      //     break;

      //   case "ignored":
      //     dispatch(getColumnSettings("IGNORED_LEASE_EVENTS_LIST", id));
      //     break;
      // }
    }

    getUserSettings(selectedType);
  }, [user, parent]);

  useEffect(() => {
    if (columnSettings && Object.keys(columnSettings).length > 0)
      getUserSettings(selectedType);
  }, [columnSettings]);

  // Sync Redux column settings into local state so modal/table use local state and retain visibility changes
  useEffect(() => {
    if (columnSettings?.leaseEvents?.all && Array.isArray(columnSettings?.leaseEvents?.all) && columnSettings?.leaseEvents?.all.length > 0) {
      setColumnOrderSettings([...columnSettings?.leaseEvents.all]);
    }
  }, [columnSettings, selectedType]);

  useEffect(() => {
    setLoadedOnce(false);
  }, []);

  useEffect(() => {
    if (selectedNotification && selectedNotification.type === LeaseEvent) {
  
      if (prevFilters) {
        prevFilters.selectedNotification = null;
        prevFilters.selectedFilters = null;      
      }
      setSelectedFilters({});
    }
  }, [selectedNotification, noticeDate]);

  useEffect(() => {
    if (
      !selectedNotification && prevFilters &&
      JSON.stringify(prevFilters?.selectedNotification) !==
        JSON.stringify(selectedNotification)
    ) {
      applyOnlySavedFilter(savedFilterSettings);
    }
  }, [selectedNotification]);

  const getEvents = (request) => {
    setLoading(true);

    let actionFn = () => {};

    switch (selectedType) {
      case "all":
        actionFn = getLeaseEvents;
        break;

      case "snoozed":
        actionFn = getLeaseEventsSnoozed;
        break;

      case "ignored":
        actionFn = getLeaseEventsIgnored;
        break;

      default:
        actionFn = getLeaseEvents;
    }
    if (request && request.clientId !== 'null' && request.pageSize !== undefined) {
      dispatch(actionFn(request))
        .catch((err) => {
          setAlertError(`Error: ${err}`);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const getUserSettings = (selectedType) => {
    if (
      columnSettings?.leaseEvents &&
      Object.keys(columnSettings?.leaseEvents.all).length > 0
    ) {
      const obj = {};

      for (
        let i = 0;
        i < columnSettings?.leaseEvents.all.length;
        i++
      ) {
        const { column, isVisible } =
          columnSettings?.leaseEvents.all[i];
        if (column !== "actions") obj[column] = isVisible;
      }

      nonHideableColumns.forEach((n) => {
        if (!obj[n]) {
          obj[n] = true;
        }
      });

      // Add default columns to list if not there
      Object.keys(defaultColumnVisibility).forEach((k) => {
        if (obj[k] === undefined) {
          obj[k] = defaultColumnVisibility[k];
        }
      });

      setcolumnVisibilityModel({
        ...columnVisibilityModel,
        leaseEvents: obj,
      });
      
      setcolumnVisibilityByType(obj);

      // switch (selectedType) {
      //   case "all":
      //   default:
      //     setcolumnVisibilityModel({
      //       ...columnVisibilityModel,
      //       leaseEvents: obj,
      //     });

      //     setcolumnVisibilityByType(obj);
      //     break;

      //   case "snoozed":
      //     setcolumnVisibilityModel({
      //       ...columnVisibilityModel,
      //       leaseEventsSnoozed: obj,
      //     });

      //     setcolumnVisibilityByType(obj);
      //     break;

      //   case "ignored":
      //     setcolumnVisibilityModel({
      //       ...columnVisibilityModel,
      //       leaseEventsIgnored: obj,
      //     });

      //     setcolumnVisibilityByType(obj);
      //     break;
      // }
    } else {
      setcolumnVisibilityModel({
        leaseEvents: defaultColumnVisibility,
        leaseEventsSnoozed: defaultColumnVisibility,
        leaseEventsIgnored: defaultColumnVisibility,
      });

      setcolumnVisibilityByType(defaultColumnVisibility);
    }
  };

  const createColumnVisibiltyData = (newModel) => {
    let columnVisibilityData = [];
    Object.entries(newModel).map((value) => {
      if (value[0] != "__check__")
        columnVisibilityData.push({ column: value[0], isVisible: value[1] });
    });
    return columnVisibilityData;
  };

  const handleColumnVisibilityModelChange = (model, selectedType) => {
    let newModel = {};

    const { userEmailAddress, id } = user;
   
    newModel = { ...columnVisibilityModel.leaseEvents, ...model };
    setcolumnVisibilityModel({
      ...columnVisibilityModel,
      leaseEvents: newModel,
    });
   
    dispatch(
      setColumnVisibility({
        data: createColumnVisibiltyData(newModel),
        tableName: savedFilterTableName,
        userId: id,
        userEmail: userEmailAddress,
      })
    )
      .then(() => {})
      .catch((err) => {
        // displayError(`Error: ${err}`);
      })
      .finally(() => {
        // setLoading(false);
      });
    // switch (selectedType) {
    //   case "all":
    //   default:
    //     newModel = { ...columnVisibilityModel.leaseEvents, ...model };
    //     setcolumnVisibilityModel({
    //       ...columnVisibilityModel,
    //       leaseEvents: newModel,
    //     });
    //     dispatch(
    //       setColumnVisibility({
    //         data: createColumnVisibiltyData(newModel),
    //         tableName: "LEASE_EVENTS_LIST",
    //         userId: id,
    //         userEmail: userEmailAddress,
    //       })
    //     )
    //       .then(() => {})
    //       .catch((err) => {
    //         // displayError(`Error: ${err}`);
    //       })
    //       .finally(() => {
    //         // setLoading(false);
    //       });
    //     break;

    //   case "snoozed":
    //     newModel = { ...columnVisibilityModel.leaseEventsSnoozed, ...model };
    //     setcolumnVisibilityModel({
    //       ...columnVisibilityModel,
    //       leaseEventsSnoozed: newModel,
    //     });
    //     dispatch(
    //       setColumnVisibility({
    //         data: createColumnVisibiltyData(newModel),
    //         tableName: "SNOOZED_LEASE_EVENTS_LIST",
    //         userId: id,
    //         userEmail: userEmailAddress,
    //       })
    //     )
    //       .then(() => {})
    //       .catch((err) => {
    //         // displayError(`Error: ${err}`);
    //       })
    //       .finally(() => {
    //         // setLoading(false);
    //       });
    //     break;

    //   case "ignored":
    //     newModel = { ...columnVisibilityModel.leaseEventsIgnored, ...model };
    //     setcolumnVisibilityModel({
    //       ...columnVisibilityModel,
    //       leaseEventsIgnored: newModel,
    //     });
    //     dispatch(
    //       setColumnVisibility({
    //         data: createColumnVisibiltyData(newModel),
    //         tableName: "IGNORED_LEASE_EVENTS_LIST",
    //         userId: id,
    //         userEmail: userEmailAddress,
    //       })
    //     )
    //       .then(() => {})
    //       .catch((err) => {
    //         // displayError(`Error: ${err}`);
    //       })
    //       .finally(() => {
    //         // setLoading(false);
    //       });
    //     break;
    // }
  };

  const handleColumnVisibilityByTypeChange = (model, selectedType) => {
   
    setcolumnVisibilityByType({ ...columnVisibilityByType, ...model });

    const newModel = { ...columnVisibilityByType, ...model };
    const { userEmailAddress, id } = user;
    let tableName = savedFilterTableName;

    // Create column visibility data with order preserved
    let columnVisibilityData = [];
    
    Object.entries(newModel).map((value) => {
      if (value[0] != "__check__")
        columnVisibilityData.push({ column: value[0], isVisible: value[1] });
    });
    // If no existing settings, create from newModel
    const currentSettings = columnOrderSettings && columnOrderSettings.length > 0
    ? columnOrderSettings
    : columnSettings?.leaseEvents?.all || [];


    const updatedColumnOrderSettings = currentSettings.map(col => {
      const visibilityData = columnVisibilityData.find(c => c.column === col.column);
      if (visibilityData) {
        return { ...col, isVisible: visibilityData.isVisible };
      }
      return col;
    });

    // If no existing settings, create from newModel
    if (updatedColumnOrderSettings.length === 0) {
      Object.entries(newModel).forEach(([column, isVisible], index) => {
        if (column !== "__check__") {
          updatedColumnOrderSettings.push({ 
            column, 
            isVisible, 
            order: index 
          });
        }
      });
    }

    setColumnOrderSettings(updatedColumnOrderSettings);
   
      setTimeout(() => {
        validateColumnChanges(updatedColumnOrderSettings);
      }, 0);
    

    dispatch(
      setColumnVisibility({
        data: columnVisibilityData,
        tableName,
        userId: id,
        userEmail: userEmailAddress,
      })
    );
    // Validate changes after state update
  };

  const setFilterOptions = (name, options, useIdMap) => {
    var filter = filters[filters.findIndex((f) => f.name === name)];

    filter.options = options;

    if (useIdMap) {
      if (filter.mapId) {
        filter.options = options.map((o) => o[filter.mapName]);
      } else {
        filter.options = options.map((o) => o.name);
      }

      filter.idMap = options;
    }

    setFilters([...filters]);
  };

  const applySavedFilters = (filterSettings) => {
   
    let userSelectedFilters = {};

    filterSettings.forEach((f) => {
      const filter = filters.find((_) => _.column === f.column);

      if (filter) {
        const settings = JSON.parse(f.value);

        userSelectedFilters[filter.name] = settings;

        // if (filter.column === "noticeDate")
        //   setSelectedNoticeDateRange(settings);
      }
    });

    setSelectedFilters(userSelectedFilters);
  };

  const applyOnlySavedFilter = (filterSettings) => {
    let userSelectedFilters = {};
    filterSettings.forEach((f) => {
      const filter = filters.find((_) => _.column === f.column);

      if (filter) {
        const settings = JSON.parse(f.value);

        userSelectedFilters[filter.name] = settings;

        // if (filter.column === "noticeDate")
        //   setSelectedNoticeDateRange(settings);
      }
    });

    setSelectedFilters(userSelectedFilters);
  };

  const buildFilterSettingsForView = useCallback(
    (filterState) => {
      const settings = [];
      const selected = filterState || selectedFilters || {};
      filters.forEach((filter) => {
        const selectedFilter = selected[filter.name];
        if (
          selectedFilter !== undefined &&
          selectedFilter !== null &&
          (Array.isArray(selectedFilter) ? selectedFilter.length > 0 : true)
        ) {
          settings.push({
            column: filter.column,
            value: JSON.stringify(selectedFilter),
          });
        }
      });
      return settings;
    },
    [filters, selectedFilters]
  );

  const handleAddViewDialogClose = useCallback(() => {
    setAddViewDialogOpen(false);
  }, []);

  const handleCloseViewDrawer = useCallback(() => {
    setViewDrawerOpen(false);
  }, []);

  const loadViewSettings = useCallback(
    (viewId) => {
      const { userEmailAddress, id } = user;
      if (!userEmailAddress) return;
      
      console.log('[VIEW LOAD] Loading settings for view:', viewId);
      
      isLoadingViewSettings.current = true;
      isViewTransitioning.current = true;
      setAddViewEnabled(false);
      setUpdateViewEnabled(false);
      setLoadedViewFilters(null);
      setLoadedViewColumns(null);
      
      // Reset page
      setCurrentPage({
        leaseEvents: 1,
        leaseEventsSnoozed: 1,
        leaseEventsIgnored: 1,
      });
      prevRequestRef.current = null;
      
      Promise.all([
        dispatch(getColumnSettings(savedFilterTableName, id, viewId)),
        dispatch(
          getFilterSettings(savedFilterTableName, userEmailAddress, viewId)
        ),
      ])
        .then(() => {
          console.log('[VIEW LOAD] Settings loaded successfully');
          
          if (viewId === selectedViewIdRef.current) {
            lastLoadedViewId.current = viewId;
          }
          
          isLoadingViewSettings.current = false;
          isViewTransitioning.current = false;
          
          // Trigger data load AFTER settings are in Redux
          setTriggerLeasesLoad((p) => p + 1);
        })
        .catch(() => {
          isLoadingViewSettings.current = false;
          isViewTransitioning.current = false;
        });
    },
    [user, savedFilterTableName, dispatch]
  );

  const handleSelectView = useCallback(
    (view) => {
      if (view) {
        const isSameView = view.id === appliedViewId;
        dispatch(userActions.resetSelectedNotificationComment());
        setAppliedViewId(view.id);
        setLastAddedViewName(view.viewName);
        setAddViewEnabled(false);
        setUpdateViewEnabled(false);
        if (isSameView && userFiltersLoaded.current && initialLoadComplete.current && loadViewSettings) {
          loadViewSettings(view.id);
        }
      }
    },
    [appliedViewId, loadViewSettings]
  );

  const handleSetDefaultView = useCallback(
    (view) => {
      if (!view) return;
      const userViewPreference = {
        id: 0,
        viewId: view.id,
        userId: user.id,
        clientId: selectedClientId,
        isDefault: true,
        createdDate: new Date().toISOString(),
        createdBy: user.userEmailAddress,
        modifiedDate: new Date().toISOString(),
        modifiedBy: user.userEmailAddress,
        pageName : savedFilterTableName,
      };
      dispatch(
        setViewAsDefault({
          tableName: savedFilterTableName,
          userViewPreference,
        })
      )
        .then(() => {
          dispatch(
            getUserViewSettings(savedFilterTableName, user.id, selectedClientId)
          );
          setAppliedViewId(view.id);
          setLastAddedViewName(view.viewName);
        })
        .catch((err) => {
          setAlertError(`Error: ${err}`);
        });
    },
    [user, savedFilterTableName, selectedClientId, dispatch]
  );

  const handleRenameViewClick = useCallback(
    (view) => {
      const updatedView = {
        ...view,
        userId: user.id,
        modifiedBy: user.userEmailAddress,
      };
      dispatch(
        updateUserViewSetting({
          tableName: savedFilterTableName,
          userViewSetting: updatedView,
        })
      )
        .then(() => {
          dispatch(
            getUserViewSettings(savedFilterTableName, user.id, selectedClientId)
          );
          if (appliedViewId === view.id) {
            setLastAddedViewName(view.viewName);
          }
        })
        .catch((err) => {
          setAlertError(`Error: ${err}`);
        });
    },
    [user, savedFilterTableName, dispatch, appliedViewId]
  );

  const handleShareToggleView = useCallback((view, action) => {
    if (action === "makePrivate") {
      setViewToMakePrivate(view);
    } else if (action === "restrict") {
      setViewToRestrict(view);
    } else if (action === "unrestrict") {
      setViewToUnrestrict(view);
    } else {
      setViewToShare(view);
      setShareRestricted(view.isRestricted || false);
    }
  }, []);

  const handleDeleteViewClick = useCallback((view) => {
    setViewToDelete(view);
  }, []);

  const handleCancelDeleteView = useCallback(() => {
    setViewToDelete(null);
  }, []);

  const handleConfirmDeleteView = useCallback(() => {
    if (!viewToDelete) return;
    const viewId = viewToDelete.id;
    const isAppliedView = appliedViewId === viewId;
    setViewToDelete(null);
    if (isAppliedView) {
      isViewTransitioning.current = true;
      setAddViewEnabled(false);
      setUpdateViewEnabled(false);
    }
    dispatch(deleteUserViewSetting(viewId, user.userEmailAddress))
      .then(() => {
        if (isAppliedView) {
          setAppliedViewId(null);
        }
        dispatch(
          getUserViewSettings(savedFilterTableName, user.id, selectedClientId)
        );
        if (isAppliedView) {
          setLoadedViewFilters(null);
          setLoadedViewColumns(null);
        }
      })
      .catch((err) => {
        setAlertError(`Error: ${err}`);
      });
  }, [
    viewToDelete,
    appliedViewId,
    user,
    savedFilterTableName,
    dispatch,
    selectedClientId,
  ]);

  const handleCancelShareView = useCallback(() => {
    setViewToShare(null);
  }, []);

  const handleConfirmShareView = useCallback(() => {
    if (!viewToShare) return;
    dispatch(
      updateUserViewSetting({
        tableName: savedFilterTableName,
        userViewSetting: {
          ...viewToShare,
          isPrivate: false,
          isRestricted: shareRestricted,
        },
      })
    )
      .then(() => {
        dispatch(
          getUserViewSettings(savedFilterTableName, user.id, selectedClientId)
        );
        setViewToShare(null);
      })
      .catch((err) => {
        setAlertError(`Error: ${err}`);
      });
  }, [viewToShare, shareRestricted, user, savedFilterTableName, dispatch]);

  const handleCancelMakePrivate = useCallback(() => {
    setViewToMakePrivate(null);
  }, []);

  const handleConfirmMakePrivate = useCallback(() => {
    if (!viewToMakePrivate) return;
    dispatch(
      updateUserViewSetting({
        tableName: savedFilterTableName,
        userViewSetting: { ...viewToMakePrivate, isPrivate: true, isRestricted: false },
      })
    )
      .then(() => {
        dispatch(
          getUserViewSettings(savedFilterTableName, user.id, selectedClientId)
        );
        setViewToMakePrivate(null);
      })
      .catch((err) => {
        setAlertError(`Error: ${err}`);
      });
  }, [viewToMakePrivate, user, savedFilterTableName, dispatch]);

  const handleCancelRestrict = useCallback(() => {
    setViewToRestrict(null);
  }, []);

  const handleConfirmRestrict = useCallback(() => {
    if (!viewToRestrict) return;
    dispatch(
      updateUserViewSetting({
        tableName: savedFilterTableName,
        userViewSetting: { ...viewToRestrict, isRestricted: true },
      })
    )
      .then(() => {
        dispatch(
          getUserViewSettings(savedFilterTableName, user.id, selectedClientId)
        );
        setViewToRestrict(null);
      })
      .catch((err) => {
        setAlertError(`Error: ${err}`);
      });
  }, [viewToRestrict, user, savedFilterTableName, dispatch]);

  const handleCancelUnrestrict = useCallback(() => {
    setViewToUnrestrict(null);
  }, []);

  const handleConfirmUnrestrict = useCallback(() => {
    if (!viewToUnrestrict) return;
    dispatch(
      updateUserViewSetting({
        tableName: savedFilterTableName,
        userViewSetting: { ...viewToUnrestrict, isRestricted: false },
      })
    )
      .then(() => {
        dispatch(
          getUserViewSettings(savedFilterTableName, user.id, selectedClientId)
        );
        setViewToUnrestrict(null);
      })
      .catch((err) => {
        setAlertError(`Error: ${err}`);
      });
  }, [viewToUnrestrict, user, savedFilterTableName, dispatch]);

  const handleSuccessAlertClose = useCallback(() => {
    setSuccessAlertOpen(false);
    setSuccessAlertMessage("");
  }, []);

  const handleAddViewClick = useCallback(() => {
    setAddViewDialogOpen(true);
  }, []);

  const handleViewButtonClick = useCallback(() => {
    setViewDrawerOpen(true);
  }, []);

  const handleUpdateView = useCallback(() => {
    if (!selectedViewId) {
      setAlertError("No view selected to update.");
      return;
    }
    const currentView = views.find((v) => v.id === selectedViewId);
    if (!currentView) {
      setAlertError("Selected view not found.");
      return;
    }
    const settings = buildFilterSettingsForView(selectedFilters);
    dispatch(
      setFilterSettings({
        tableName: savedFilterTableName,
        userEmailAddress: user.userEmailAddress,
        settings,
      })
    )
      .then(() => {
        const updatedView = {
          ...currentView,
          userId: user.id,
          modifiedBy: user.userEmailAddress,
          modifiedDate: new Date().toISOString(),
        };
        return dispatch(
          updateUserViewSetting({
            tableName: savedFilterTableName,
            userViewSetting: updatedView,
          },
          true)
        );
      })
      .then(() => {
        dispatch(
          getUserViewSettings(savedFilterTableName, user.id, selectedClientId)
        );
        setAddViewEnabled(false);
        setUpdateViewEnabled(false);
        setLoadedViewFilters(null);
        setLoadedViewColumns(null);
        setSuccessAlertMessage("View updated successfully.");
        setSuccessAlertOpen(true);
      })
      .catch((err) => {
        setAlertError(`Error updating view: ${err}`);
      });
  }, [
    selectedViewId,
    views,
    selectedFilters,
    buildFilterSettingsForView,
    user,
    savedFilterTableName,
    dispatch,
  ]);

  const handleAddViewSave = useCallback(
    (viewData) => {
      const { name, shareWithOther, restricted, setAsDefault } = viewData;
      setAddViewDialogOpen(false);
      const filterSettings = buildFilterSettingsForView(selectedFilters || {});
      dispatch(
        setFilterSettings({
          tableName: savedFilterTableName,
          userEmailAddress: user.userEmailAddress,
          settings: filterSettings,
        })
      ).then(() => {
        const newView = {
          id: 0,
          viewName: name,
          tableName: savedFilterTableName,
          isPrivate: !shareWithOther,
          isRestricted: restricted,
          isDefault: setAsDefault,
          userId: user.id,
          clientId: selectedClientId,
          createdBy: user.userEmailAddress,
          createdDate: new Date().toISOString(),
          modifiedBy: user.userEmailAddress,
          modifiedDate: new Date().toISOString(),
          isOwner: true,
        };
        return dispatch(
          saveUserViewSetting({
            tableName: savedFilterTableName,
            userViewSetting: newView,
          })
        );
      })
      .then((newViewResp) => {
        const newViewId =
          newViewResp?.payload?.data ?? newViewResp?.data ?? newViewResp;
        if (setAsDefault && newViewId) {
          const userViewPreference = {
            id: 0,
            viewId: newViewId,
            userId: user.id,
            clientId: selectedClientId,
            isDefault: true,
            createdDate: new Date().toISOString(),
            createdBy: user.userEmailAddress,
            modifiedDate: new Date().toISOString(),
            modifiedBy: user.userEmailAddress,
            pageName : savedFilterTableName,
          };
          return dispatch(
            setViewAsDefault({
              tableName: savedFilterTableName,
              userViewPreference,
            })
          ).then(() => newViewId);
        }
        return newViewId;
      })
      .then((newViewId) => {
        return dispatch(
          getUserViewSettings(savedFilterTableName, user.id, selectedClientId)
        ).then(() => {
          if (newViewId) {
            setAppliedViewId(newViewId);
            setLastAddedViewName(name);
          }
          setAddViewEnabled(false);
          setUpdateViewEnabled(false);
          setLoadedViewFilters(null);
          setLoadedViewColumns(null);
          setSuccessAlertMessage("New view is successfully saved.");
          setSuccessAlertOpen(true);
        });
      })
      .catch((err) => {
        setAlertError(`Error saving view: ${err}`);
      });
    },
    [
      selectedFilters,
      buildFilterSettingsForView,
      user,
      savedFilterTableName,
      selectedClientId,
      dispatch,
    ]
  );

  let prevFilters = usePrevious({
    selectedFilters,
    searchKeyword,
    selectedNoticeDateRange,
    selectedClientId,
    selectedNotification
  });

  // const prevFiltersRef = useRef(null);
   
  useEffect(() => {
    if (!user || !user.userEmailAddress || !selectedClientId || selectedClientId <= 0) {
      return;
    }

    const isClientChange = prevClientIdRef.current !== null && prevClientIdRef.current !== selectedClientId;
    const isInitialLoad = prevClientIdRef.current === null;

    if (!isInitialLoad && !isClientChange) {
      return;
    }

    console.log('[INITIALIZATION]', isInitialLoad ? 'Initial load' : 'Client change', {
      from: prevClientIdRef.current,
      to: selectedClientId
    });

    prevClientIdRef.current = selectedClientId;
    
    // Reset all states
    setLoadedOnce(false);
    setLoadedViewFilters(null);
    setLoadedViewColumns(null);
    setAppliedViewId(null);
    setLastAddedViewName("");
    setAddViewEnabled(false);
    setUpdateViewEnabled(false);
    setIsInitialFilterLoad(false);
    setSearchKeyword("");
    setSearchValue("");
    setSelectedIds([]);
    setAlertMessage("");
    setAlertError("");
    setCurrentPage({
      leaseEvents: 1,
      leaseEventsSnoozed: 1,
      leaseEventsIgnored: 1,
    });
    
    // Reset all refs
    lastLoadedViewId.current = null;
    isViewTransitioning.current = false;
    prevRequestRef.current = null;
    hasInitialViewLoad.current = false;
    isLoadingViewSettings.current = true;
    initialLoadComplete.current = false;
    userFiltersLoaded.current = false;

    // Load filter options on client change (page size is loaded separately via userId effect)
    getFilterOptions();
    
    const { userEmailAddress, id } = user;

    dispatch(getUserViewSettings(savedFilterTableName, id, selectedClientId))
      .then((response) => {
        hasInitialViewLoad.current = true;
        const fetchedViews = response?.payload?.data || [];
        const defaultView = fetchedViews.find((v) => v.isDefault === true);
        const viewIdToUse = defaultView ? defaultView.id : null;
        if (defaultView) {
          setAppliedViewId(defaultView.id);
          setLastAddedViewName(defaultView.viewName);
        }
        
        console.log('[INITIALIZATION] Loading settings for view:', viewIdToUse);
        
        return Promise.all([
          dispatch(
            getColumnSettings(savedFilterTableName, id, viewIdToUse)
          ),
          dispatch(
            getFilterSettings(savedFilterTableName, userEmailAddress, viewIdToUse)
          ),
        ]).then(() => viewIdToUse);
      })
      .then((viewIdToUse) => {
        lastLoadedViewId.current = viewIdToUse ?? null;
        isLoadingViewSettings.current = false;
        initialLoadComplete.current = true;
        userFiltersLoaded.current = true;
        
        console.log('[INITIALIZATION] Settings loaded successfully');
        
        // Trigger lease events load after initialization
        setTriggerLeasesLoad(prev => prev + 1);
      })
      .catch(() => {
        hasInitialViewLoad.current = true;
        lastLoadedViewId.current = null;
        Promise.all([
          dispatch(getColumnSettings(savedFilterTableName, id, null)),
          dispatch(
            getFilterSettings(savedFilterTableName, userEmailAddress, null)
          ),
        ]).finally(() => {
          isLoadingViewSettings.current = false;
          initialLoadComplete.current = true;
          userFiltersLoaded.current = true;
          
          // Trigger lease events load even on error
          setTriggerLeasesLoad(prev => prev + 1);
        });
      });
  }, [user, parent, selectedClientId, savedFilterTableName]);

  useEffect(() => {
    selectedViewIdRef.current = selectedViewId;
  }, [selectedViewId]);

  useEffect(() => {
    // Skip on initial load
    if (!userFiltersLoaded.current || !initialLoadComplete.current) {
      console.log('[VIEW SWITCH SKIP] Initial load not complete');
      return;
    }
    
    // Skip if already loaded this view
    if (lastLoadedViewId.current === selectedViewId) {
      console.log('[VIEW SWITCH SKIP] Same view already loaded');
      return;
    }
    
    console.log('[VIEW SWITCH] Switching to view:', selectedViewId);
    
    if (user && user.userEmailAddress) {
      loadViewSettings(selectedViewId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedViewId]);

  // Reset Add/Update button states when view or client changes
  useEffect(() => {
    setAddViewEnabled(false);
    setUpdateViewEnabled(false);
  }, [selectedViewId, selectedClientId]);

  // Clear saved baseline when client changes so capture effect re-runs; otherwise
  // loadedViewFilters/Columns stay from the previous client and falsely enable Add/Update view.
  useEffect(() => {
    setLoadedViewFilters(null);
    setLoadedViewColumns(null);
  }, [selectedClientId]);

  useEffect(() => {
    if (isViewTransitioning.current || isLoadingViewSettings.current) {
      console.log('[CAPTURE SKIP] Still loading/transitioning');
      return;
    }
    
    if (!initialLoadComplete.current || !userFiltersLoaded.current) {
      console.log('[CAPTURE SKIP] Not ready yet');
      return;
    }
    
    // Only capture once per view
    if (loadedViewFilters !== null || loadedViewColumns !== null) {
      console.log('[CAPTURE SKIP] Already captured');
      return;
    }
    
    if (!savedFilterSettings || !columnSettings?.leaseEvents?.all) {
      return;
    }
    
    const captureTimer = setTimeout(() => {
      console.log('[CAPTURE] Capturing baseline settings');
      setLoadedViewFilters(JSON.parse(JSON.stringify(selectedFilters || {})));
      const cols = columnSettings?.leaseEvents?.all;
      // Match Lease List: baseline columns from local columnOrderSettings so re-capture after
      // Update View does not overwrite the grid with stale Redux (setColumnOrder does not update Redux).
      if (columnOrderSettings && columnOrderSettings.length > 0) {
        setLoadedViewColumns(
          JSON.parse(
            JSON.stringify(
              columnOrderSettings.map((c) => ({
                column: c.column || c.name,
                isVisible: c.isVisible,
                order: c.order,
              }))
            )
          )
        );
      } else if (cols && cols.length > 0) {
        const colCopy = cols.map((c) => ({
          column: c.column || c.name,
          isVisible: c.isVisible,
          order: c.order,
        }));
        setLoadedViewColumns(colCopy);
      }
    }, 300); // Increased from 200ms

    return () => clearTimeout(captureTimer);
  }, [
    parent,
    savedFilterSettings,
    columnSettings?.leaseEvents?.all,
    loadedViewFilters,
    loadedViewColumns,
    selectedFilters,
    columnOrderSettings,
  ]);

  const validateFilterChanges = useCallback(
    (filterState) => {
      if (
        loadedViewFilters === null ||
        isViewTransitioning.current
      ) {
        return;
      }
      
      // Exclude noticeDateRange from comparison
      const filterStateWithoutNoticeDate = { ...filterState };
      delete filterStateWithoutNoticeDate.noticeDateRange;
      
      const loadedFiltersWithoutNoticeDate = { ...loadedViewFilters };
      delete loadedFiltersWithoutNoticeDate.noticeDateRange;
      
      const filtersChanged =
        JSON.stringify(filterStateWithoutNoticeDate || {}) !== JSON.stringify(loadedFiltersWithoutNoticeDate || {});
      if (filtersChanged) {
        setAddViewEnabled(true);
        setUpdateViewEnabled(
          !!selectedViewId && appliedViewId === selectedViewId && canUpdateCurrentView
        );
      } else {
        setAddViewEnabled(false);
        setUpdateViewEnabled(false);
      }
    },
    [
      parent,
      loadedViewFilters,
      selectedViewId,
      appliedViewId,
      canUpdateCurrentView,
    ]
  );

  const validateColumnChanges = useCallback(
    (columnSettingsArray) => {
      const normalizeAndSortByOrder = (arr) =>
        (arr || [])
          .filter(c => (c.column || c.name) !== "lastCommentCreatedDate")
          .map((c, idx) => ({
            column: c.column || c.name,
            isVisible: c.isVisible,
            order: c.order,
            __idx: idx, // preserve existing relative order when order is null/undefined
          }))
          .sort((a, b) => {
            const aHasOrder = a.order !== null && a.order !== undefined;
            const bHasOrder = b.order !== null && b.order !== undefined;

            // If both have order, sort by it.
            if (aHasOrder && bHasOrder) {
              return a.order - b.order;
            }
            // If neither has order, keep original sequence.
            if (!aHasOrder && !bHasOrder) {
              return a.__idx - b.__idx;
            }
            // If only one has order, keep relative sequence by existing position.
            return a.__idx - b.__idx;
          })
          .map(({ __idx, ...rest }) => rest);

      const normalizedLoaded = normalizeAndSortByOrder(loadedViewColumns);
      const normalizedCurrent = normalizeAndSortByOrder(columnSettingsArray);

      const columnsChanged =
        JSON.stringify(normalizedCurrent) !== JSON.stringify(normalizedLoaded);
      
        if (columnsChanged) {
        setAddViewEnabled(true);
        setUpdateViewEnabled(
          !!selectedViewId && appliedViewId === selectedViewId && canUpdateCurrentView
        );
      } else {
        setAddViewEnabled(false);
        setUpdateViewEnabled(false);
      }
    },
    [
      parent,
      loadedViewColumns,
      selectedViewId,
      appliedViewId,
      canUpdateCurrentView,
    ]
  );

  useEffect(() => {
    if (
      !initialLoadComplete.current ||
      !userFiltersLoaded.current ||
      isViewTransitioning.current ||
      loadedViewFilters === null ||
      loadedViewColumns === null
    ) {
      return;
    }
    
    // Exclude noticeDateRange from comparison
    const currentFiltersWithoutNoticeDate = { ...selectedFilters };
    delete currentFiltersWithoutNoticeDate.noticeDateRange;
    
    const loadedFiltersWithoutNoticeDate = { ...loadedViewFilters };
    delete loadedFiltersWithoutNoticeDate.noticeDateRange;
    
    const filtersChanged =
      JSON.stringify(currentFiltersWithoutNoticeDate || {}) !== JSON.stringify(loadedFiltersWithoutNoticeDate || {});
    const cols =
      columnOrderSettings && columnOrderSettings.length > 0
        ? columnOrderSettings
        : columnSettings?.leaseEvents?.all || [];
    const normalizedLoaded = (loadedViewColumns || []).map((c) => ({
      column: c.column || c.name,
      isVisible: c.isVisible,
      order: c.order,
    }));
    const normalizedCurrent = cols.map((c) => ({
      column: c.column || c.name,
      isVisible: c.isVisible,
      order: c.order,
    }));
    const columnsChanged =
      JSON.stringify(normalizedCurrent) !== JSON.stringify(normalizedLoaded);
    if (filtersChanged || columnsChanged) {
      setAddViewEnabled(true);
      setUpdateViewEnabled(
        !!selectedViewId && appliedViewId === selectedViewId && canUpdateCurrentView
      );
    } else {
      setAddViewEnabled(false);
      setUpdateViewEnabled(false);
    }
  }, [
    parent,
    selectedFilters,
    columnOrderSettings,
    columnSettings?.leaseEvents?.all,
    loadedViewFilters,
    loadedViewColumns,
    selectedViewId,
    appliedViewId,
    canUpdateCurrentView,
  ]);

  useEffect(() => {
    if (
      !initialLoadComplete.current ||
      !userFiltersLoaded.current ||
      isViewTransitioning.current ||
      loadedViewFilters === null ||
      loadedViewColumns === null
    ) {
      return;
    }
    const hasOwned = views.some((v) => v.isOwner === true || v.isDefault === true);
    if (hasOwned) return;
    const hasFilters =
      selectedFilters &&
      typeof selectedFilters === "object" &&
      Object.keys(selectedFilters).length > 0 &&
      Object.values(selectedFilters).some((v) => {
        if (Array.isArray(v)) return v.length > 0;
        if (typeof v === "boolean") return v === true;
        return v != null;
      });
    const hasCustomColumns =
      columnOrderSettings &&
      columnOrderSettings.length > 0 &&
      columnOrderSettings.some((col) => {
        const def = defaultColumnVisibility[col.column];
        return def !== undefined && col.isVisible !== def;
      });
    if (hasFilters || hasCustomColumns) {
      setAddViewEnabled(true);
    }
  }, [
    parent,
    views,
    selectedFilters,
    columnOrderSettings,
    defaultColumnVisibility,
    loadedViewFilters,
    loadedViewColumns,
  ]);

  useEffect(() => {
    if (
      views.length === 0 ||
      !user?.id
    ) {
      return;
    }
    const hasDefault = views.some((v) => v.isDefault === true);
    if (hasDefault) return;
    const owned = views.filter((v) => v.isOwner === true);
    if (owned.length === 0) return;
    const sorted = [...owned].sort((a, b) =>
      a.viewName.localeCompare(b.viewName)
    );
    const first = sorted[0];
    dispatch(
      setViewAsDefault({
        tableName: savedFilterTableName,
        userViewPreference: {
          id: 0,
          viewId: first.id,
          userId: user.id,
          clientId: selectedClientId,
          isDefault: true,
          createdDate: new Date().toISOString(),
          createdBy: user.userEmailAddress,
          modifiedDate: new Date().toISOString(),
          modifiedBy: user.userEmailAddress,
          pageName : savedFilterTableName,
        },
      })
    )
      .then(() =>
        dispatch(
          getUserViewSettings(savedFilterTableName, user.id, selectedClientId)
        )
      )
      .catch(() => {});
  }, [parent, views, user, savedFilterTableName, selectedClientId, dispatch]);

  // Apply filters from savedFilterSettings when they change (initial load or view switch) - match Lease List
  // Skip pushing saved filters into local state while a lease-event notification drives notice-date-only mode;
  // when the notification clears, re-apply so the list matches the selected view again.
  useEffect(() => {
    if (userFiltersLoaded.current && savedFilterSettings && savedFilterSettings.length >= 0) {
      if (disableOtherFilter) {
        return;
      }
      console.log('[APPLY FILTERS] Applying saved filter settings', {
        count: savedFilterSettings.length,
        viewId: selectedViewId
      });
      
      if (savedFilterSettings.length > 0) {
        applySavedFilters(savedFilterSettings);
      } else {
        setSelectedFilters({});
      }
      
      // Reset page and clear request cache
      setCurrentPage({
        leaseEvents: 1,
        leaseEventsSnoozed: 1,
        leaseEventsIgnored: 1,
      });
      prevRequestRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedFilterSettings, userFiltersLoaded.current, selectedViewId, views.length, disableOtherFilter]);

  // Refresh view settings when returning to the lease events page (match Lease List)
  useEffect(() => {
    // Only run after initial load
    if (!hasInitialViewLoad.current) {
      return;
    }
    
    // Check if returning to this page
    if (location.pathname !== lastLocationPath.current && user && user.id) {
      console.log('[PAGE NAV] Returning to page - refreshing views');
      lastLocationPath.current = location.pathname;
      
      // Just refresh user view settings - the savedFilterSettings effect will handle the rest
      dispatch(getUserViewSettings(savedFilterTableName, user.id, selectedClientId));
    }
  }, [location.pathname, user, savedFilterTableName, dispatch, selectedClientId]);

  // Single unified data loading effect - triggers on savedFilterSettings, triggerLeasesLoad, or filter changes (match Lease List)
  useEffect(() => {
    if (isLoadingViewSettings.current) {
      console.log('[DATA FETCH SKIP] Still loading view settings');
      return;
    }
    
    const canRun = 
      selectedClientId && 
      userFiltersLoaded.current && 
      isPageSizeUpdated && 
      !isInitialFilterLoad;
    
    if (!canRun) {
      console.log('[DATA FETCH SKIP] Conditions not met', {
        selectedClientId: !!selectedClientId,
        userFiltersLoaded: userFiltersLoaded.current,
        isPageSizeUpdated,
        isInitialFilterLoad
      });
      return;
    }
    
    // Match Lease List: when filters exist and same client (or first load), apply them first
    // On initial load, prevFilters is undefined, but savedFilterSettings should still be applied
    const isSameClient = prevFilters && selectedClientId === prevFilters.selectedClientId;
    const isFirstLoad = !prevFilters;
    
    if (
      savedFilterSettings &&
      savedFilterSettings.length > 0 &&
      (isFirstLoad || isSameClient)
    ) {
      // Apply saved filters - the selectedFilters change will trigger the refetch effect
      console.log('[DATA FETCH] Applying saved filters', {
        viewId: selectedViewId,
        filterCount: savedFilterSettings.length,
        pageSize: leaseEventPageSize,
        isFirstLoad
      });
      applySavedFilters(savedFilterSettings);
    } else {
      // No saved filters or client changed - fetch directly
      console.log('[DATA FETCH] Fetching lease events', {
        viewId: selectedViewId,
        hasFilters: Object.keys(selectedFilters || {}).length > 0,
        triggerCount: triggerLeasesLoad,
        pageSize: leaseEventPageSize
      });
      
      const request = buildRequest();
      getEvents(request);
    }
    setLoadedOnce(true);
    setIsInitialFilterLoad(true);
  }, [savedFilterSettings, selectedClientId, isPageSizeUpdated, triggerLeasesLoad]);

  // Refetch when filters actually change after initial load
  useEffect(() => {
    if (isViewTransitioning.current || isLoadingViewSettings.current) return;
    
    const filtersOrClientChanged =
      prevFilters &&
      isInitialFilterLoad &&
      (JSON.stringify(prevFilters.selectedFilters) !==
        JSON.stringify(selectedFilters) ||
        JSON.stringify(prevFilters?.selectedNotification) !==
          JSON.stringify(selectedNotification) ||
        prevFilters.selectedClientId !== selectedClientId);
        
    if (
      selectedClientId &&
      isPageSizeUpdated &&
      userFiltersLoaded.current &&
      filtersOrClientChanged
    ) {
      console.log('[DATA FETCH] Filters changed - refetching', {
        viewId: selectedViewId
      });
      
      const request = buildRequest();
      getEvents(request);
    }
  }, [
    selectedFilters,
    selectedClientId,
    selectedNotification,
    prevFilters,
    isPageSizeUpdated,
    isInitialFilterLoad,
  ]);
  
  useEffect(() => {
    if (prevFilters && prevFilters.searchKeyword !== searchKeyword) {
      const request = buildRequest();

      getEvents(request);
    }
  }, [searchKeyword]);

  useEffect(() => {
    if (isViewTransitioning.current || isLoadingViewSettings.current) return;

    if (prevFilters && prevFilters.selectedType !== selectedType && isPageSizeUpdated && userFiltersLoaded.current) {
      const request = buildRequest();

      getEvents(request);
    }
  }, [selectedType, isPageSizeUpdated]);

  useEffect(() => {
    dispatch(getRegions());
  }, []);

  useEffect(() => {
    setFilterOptions("names", leaseNames);
  }, [leaseNames]);

  useEffect(() => {
    setFilterOptions("regionIds", regions, true);
  }, [regions]);

  useEffect(() => {
    setFilterOptions("countries", countries);
  }, [countries]);

  useEffect(() => {
    setFilterOptions("stateProvinces", stateProvinces);
  }, [stateProvinces]);

  useEffect(() => {
    setFilterOptions("cities", cities);
  }, [cities]);

  useEffect(() => {
    setFilterOptions("leaseTypes", leaseTypes);
  }, [leaseTypes]);

  useEffect(() => {
    setFilterOptions("leaseStatuses", leaseStatuses);
  }, [leaseStatuses]);

  useEffect(() => {
    setFilterOptions("eventTypes", eventTypes);
  }, [eventTypes]);

  useEffect(() => {
    setFilterOptions("eventStatuses", eventStatuses);
  }, [eventStatuses]);

  useEffect(() => {
    setFilterOptions("businessUnits", businessUnits);
  }, [businessUnits]);

  useEffect(() => {
    if (tenureTypes) {
      setFilterOptions(
        "tenureTypes",
        tenureTypes.filter((f) => f && f.toLowerCase() !== "flex")
      );
    }
  }, [tenureTypes]);

  useEffect(() => {
    if (propertyTypes) {
      setFilterOptions(
        "propertyTypes",
        propertyTypes.filter((f) => f && f.toLowerCase() !== "flex")
      );
    }
  }, [propertyTypes]);

  const handleLeaseNameClicked = (selectedLeaseEventId) => {
    setDetailsSettings({
      ...detailsSettings,
      open: true,
      selectedLeaseEventId,
    });
  };

  const handlePageChange = ({ page, pageSize }) => {
    let request = buildRequest();
    if (leaseEventPageSize > 0 && pageSize !== leaseEventPageSize) {
      updatePageSize(pageSize);
    }

    switch (selectedType) {
      case "all":
      default:
        setCurrentPage({
          ...currentPage,
          leaseEvents: page + 1,
        });

        getEvents({
          ...request,
          pageNumber: page + 1,
          pageSize: pageSize,
          ...currentSort.leaseEvents,
        });

        break;

      case "snoozed":
        setCurrentPage({
          ...currentPage,
          leaseEventsSnoozed: page + 1,
        });

        getEvents({
          ...request,
          pageNumber: page + 1,
          pageSize: pageSize,
          ...currentSort.leaseEventsSnoozed,
        });
        break;

      case "ignored":
        setCurrentPage({
          ...currentPage,
          leaseEventsIgnored: page + 1,
        });

        getEvents({
          ...request,
          pageNumber: page + 1,
          pageSize: pageSize,
          ...currentSort.leaseEventsIgnored,
        });

        break;
    }
  };

  const handleSortModelChange = (model) => {
    const sortColumn = model.length > 0 ? model[0].field : "";
    const sortDirection = model.length > 0 ? model[0].sort : "";

    let request = buildRequest();

    switch (selectedType) {
      case "all":
      default:
        setCurrentSort({
          ...currentSort,
          leaseEvents: {
            sortColumn,
            sortDirection,
          },
        });

        getEvents({
          ...request,
          pageNumber: currentPage.leaseEvents,
          pageSize: leaseEventPageSize,
          sortColumn,
          sortDirection,
        });

        break;

      case "snoozed":
        setCurrentSort({
          ...currentSort,
          leaseEventsSnoozed: {
            sortColumn,
            sortDirection,
          },
        });

        getEvents({
          ...request,
          pageNumber: currentPage.leaseEventsSnoozed,
          pageSize: leaseEventPageSize,
          sortColumn,
          sortDirection,
        });
        break;

      case "ignored":
        setCurrentSort({
          ...currentSort,
          leaseEventsIgnored: {
            sortColumn,
            sortDirection,
          },
        });

        getEvents({
          ...request,
          pageNumber: currentPage.leaseEventsIgnored,
          pageSize: leaseEventPageSize,
          sortColumn,
          sortDirection,
        });
        break;
    }
  };

  const computeNoticeDates = (rangeMonths) => {
    const noticeDateStart =
      rangeMonths && rangeMonths !== 0 ? moment().utc().format("LL") : "";
    const noticeDateEnd =
      rangeMonths && rangeMonths !== 0
        ? moment().add(rangeMonths, "months").utc().format("LL")
        : "";

        return disableOtherFilter && noticeDate 
        ? { 
            noticeDateStart: moment.utc(noticeDate).format("LL"), 
            noticeDateEnd: moment.utc(noticeDate).format("LL") 
          } 
        : { noticeDateStart, noticeDateEnd };
  };

  const handleNoticeDateRangeChange = (event) => {
    setSelectedNoticeDateRange(event.target.value);
    handleFilterSaved({
      ...selectedFilters,
      noticeDateRange: event.target.value,
    });
    setCurrentPage({
      leaseEvents: 1,
      leaseEventsSnoozed: 1,
      leaseEventsIgnored: 1,
    });
  };

  const getFilterOptions = () => {
    setLoadingFilterOptions(true);

    dispatch(getLeaseEventFilterOptions(selectedClientId)).finally(() => {
      setLoadingFilterOptions(false);
    });
  };

  const handleFilterApply = (filters) => {
    setSelectedFilters(filters);
  };

  const buildFilterSettings = (filtersToSave) => {
    let settings = [];

    Object.keys(filtersToSave).forEach((key) => {
      let selectedFilter = filtersToSave[key];

      const filter = filters.find((f) => f.name === key);

      if (filter) {
        settings.push({
          column: filter.column,
          value: JSON.stringify(selectedFilter),
        });
      }
    });

    return settings;
  };

  const handleFilterSaved = (filtersToSave) => {
    
    const noticeDateValue = filtersToSave?.noticeDateRange !== undefined
    ? filtersToSave.noticeDateRange
    : selectedNoticeDateRange;

    setSelectedFilters({
      ...filtersToSave,
      noticeDateRange: noticeDateValue,
    });

    const settings = buildFilterSettings(filtersToSave).filter(
      (s) => s.column !== "noticeDate"
    );
    settings.push({
      column: "noticeDate",
      value: String(noticeDateValue),
    });
    const { userEmailAddress } = user;

    dispatch(
      setFilterSettings({
        tableName: savedFilterTableName,
        userEmailAddress,
        settings,
      })
    );
  };

  const handleSearchKeywordChange = (keyword) => {
    setSearchKeyword(keyword);
  };

  const getActiveFilterCount = () => {
    if (disableOtherFilter) return 0;
    if (!selectedFilters || typeof selectedFilters !== "object") return 0;
    let count = 0;
    Object.keys(selectedFilters).forEach((key) => {
      if (key === "noticeDateRange") return;
      const value = selectedFilters[key];
      if (Array.isArray(value) && value.length > 0) count++;
      else if (typeof value === "boolean" && value) count++;
      else if (value && !Array.isArray(value)) count++;
    });
    return count;
  };

  const handleFilterButtonClick = () => {
    console.log('[FILTER MODAL] Opening - loading options');
    getFilterOptions();
    setFilterModalOpen(true);
  };

  const handleColumnButtonClick = () => {
    setColumnModalOpen(true);
  };

  const handleFilterModalClose = () => {
    setFilterModalOpen(false);
  };

  const handleFilterModalApply = useCallback(
    (appliedFilters) => {
      setCurrentPage({
        leaseEvents: 1,
        leaseEventsSnoozed: 1,
        leaseEventsIgnored: 1,
      });
      handleFilterSaved(appliedFilters);
      setFilterModalOpen(false);
      setTimeout(() => {
        validateFilterChanges(appliedFilters);
      }, 0);
    },
    [handleFilterSaved, validateFilterChanges]
  );

  const handleFilterModalClear = useCallback(() => {
    setCurrentPage({
      leaseEvents: 1,
      leaseEventsSnoozed: 1,
      leaseEventsIgnored: 1,
    });
    const clearedFilters = { noticeDateRange: selectedNoticeDateRange };
    setSelectedFilters(clearedFilters);
    handleFilterSaved({});
    setFilterModalOpen(false);
    setTimeout(() => {
      validateFilterChanges(clearedFilters);
    }, 0);
  }, [handleFilterSaved, validateFilterChanges, selectedNoticeDateRange]);

  const handleColumnModalClose = () => {
    setColumnModalOpen(false);
  };

  const handleColumnReset = useCallback(
    (resetColumns) => {
      const { userEmailAddress, id } = user;
      const data = resetColumns.map((col) => ({
        column: col.column,
        isVisible: col.isVisible,
        order: col.order,
      }));
      let tableName = savedFilterTableName;
      // switch (selectedType) {
      //   case "all":
      //   default:
      //     tableName = "LEASE_EVENTS_LIST";
      //     break;
      //   case "snoozed":
      //     tableName = "SNOOZED_LEASE_EVENTS_LIST";
      //     break;
      //   case "ignored":
      //     tableName = "IGNORED_LEASE_EVENTS_LIST";
      //     break;
      // }
      
      setColumnOrderSettings(data);
      if (
        loadedViewColumns !== null
      ) {
        setTimeout(() => {
          validateColumnChanges(
            resetColumns.map((c) => ({
              column: c.column,
              isVisible: c.isVisible,
              order: c.order ?? 0,
            }))
          );
        }, 0);
      }
      // Use setColumnOrder for reset (includes both visibility and order)
      dispatch(
        setColumnOrder({
          data,
          tableName: savedFilterTableName,
          userId: id,
          userEmail: userEmailAddress,
        })
      );
    },
    [
      user,
      selectedType,
      parent,
      selectedViewId,
      loadedViewColumns,
      validateColumnChanges,
    ]
  );

  const handleColumnOrderChange = useCallback(
    (settings, gridColumns) => {
      const { field, targetIndex, oldIndex } = settings;

      // Use local state so visibility changes are reflected when reordering
      const currentSettings =
        columnOrderSettings && columnOrderSettings.length > 0
          ? columnOrderSettings
          : columnSettings?.leaseEvents?.all || [];

      let newColumnOrder = gridColumns.map((g, i) => {
        const existingSetting = currentSettings.find((c) => c.column === g.field);

        let visibility;
        if (existingSetting) {
          visibility = existingSetting.isVisible;
        } else {
          visibility = defaultColumnVisibility[g.field] ?? false;
        }

        return {
          column: g.field,
          order: i,
          isVisible: visibility,
        };
      });

      const { userEmailAddress, id } = user;

      setColumnOrderSettings(newColumnOrder);

      // Run change detection so Update View button appears when order/visibility differ from loaded view
      // if (parent === "leaseEventsPage") {
        setTimeout(() => {
          validateColumnChanges(newColumnOrder);
        }, 0);
      // }

      let tableName = savedFilterTableName;
      // switch (selectedType) {
      //   case "all":
      //   default:
      //     tableName = "LEASE_EVENTS_LIST";
      //     break;
      //   case "snoozed":
      //     tableName = "SNOOZED_LEASE_EVENTS_LIST";
      //     break;
      //   case "ignored":
      //     tableName = "IGNORED_LEASE_EVENTS_LIST";
      //     break;
      // }

      // Use setColumnOrder for order changes (separate endpoint from visibility)
      dispatch(
        setColumnOrder({
          data: newColumnOrder,
          tableName,
          userId: id,
          userEmail: userEmailAddress,
        })
      );

    },
    [
      columnOrderSettings,
      columnSettings,
      selectedType,
      defaultColumnVisibility,
      user,
      parent,
      validateColumnChanges,
      selectedViewId,
      dispatch,
    ]
  );

  const COLUMN_HEADER_MAP = {
    leaseEventId: "TME Event Id",
    eventId: "Event Id",
    leaseCode: "Tenure Id",
    leaseName: "Lease Name",
    propertyName: "Property Name",
    streetAddress1: "Property Address",
    businessUnit: "Business Unit",
    countryName: "Country",
    stateProvince: "State / Province",
    city: "City",
    rentableBuildingArea: "Building Area",
    eventType: "Event Type",
    leaseEventStatusName: "Event Status",
    eventNote: "Event Note",
    tenureType: "Tenure Type",
    lastComment: "Last Comment",
    noticeDate: "Notice Date",
    actionDate: "Event Date",
    expireDate: "Lease End Date",
    snoozedUntil: "Snoozed Until",
    snoozedComment: "Snoozed Comment",
    leaseEventIgnoredDate: "Ignored Date",
    ignoreComment: "Ignore Comment",
    transactions: "Associated Transactions",
  };
  const getOrderedVisibleExportColumnHeaders = () => {
        const visible = columnVisibilityByType || {};
        const excludeFields = new Set(["actions", "__check__"]);
        const orderSource =
            columnOrderSettings && columnOrderSettings.length > 0
                ? columnOrderSettings
                : columnSettings?.leaseEvents?.all || [];
        const sortedOrder = [...orderSource].sort(
            (a, b) => (a.order ?? 999) - (b.order ?? 999)
        );
        const fieldsWithSavedOrder = new Set(
            sortedOrder.map((c) => c.column || c.name).filter(Boolean)
        );
        const seenHeaders = new Set();
        const headers = [];
        const tryAdd = (field) => {
            if (!field || excludeFields.has(field)) return;
            if (!visible[field]) return;
            const header = COLUMN_HEADER_MAP[field] || field;
            if (seenHeaders.has(header)) return;
            seenHeaders.add(header);
            headers.push(header);
        };
        sortedOrder.forEach((col) => tryAdd(col.column || col.name));
        const defaultKeyOrder = Object.keys(defaultColumnVisibility);
        Object.keys(visible)
            .filter(
                (f) =>
                    visible[f] &&
                    !excludeFields.has(f) &&
                    !fieldsWithSavedOrder.has(f)
            )
            .sort((a, b) => {
                const ia = defaultKeyOrder.indexOf(a);
                const ib = defaultKeyOrder.indexOf(b);
                if (ia === -1 && ib === -1) return String(a).localeCompare(String(b));
                if (ia === -1) return 1;
                if (ib === -1) return -1;
                return ia - ib;
            })
            .forEach(tryAdd);
        return headers;
    };
  const handleDownloadClick = () => {
    setDownloadLoading(true);
    const request = buildRequest();
    const visibleColumns = getOrderedVisibleExportColumnHeaders();
    const req = { ...request, columns: visibleColumns };
    if (selectedType === "snoozed") req.leaseEventType = "snoozed";
    if (selectedType === "ignored") req.leaseEventType = "ignored";

    const filename =
      selectedType === "all"
        ? "leaseEvent.xlsx"
        : selectedType === "snoozed"
        ? "leaseEvent_snoozed.xlsx"
        : "leaseEvent_ignored.xlsx";
    dispatch(getLeaseEventSpreadsheet(req))
      .then((response) => {
        const payload = response?.payload?.data ?? response?.data ?? response;
        const href = URL.createObjectURL(payload);
        const link = document.createElement("a");
        link.href = href;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
      })
      .catch(() => {
        setAlertError("Download failed");
      })
      .finally(() => {
        setDownloadLoading(false);
      });
  };

  const renderLeaseEventsHeader = () => (
    <Box className="lease-events-list-header-section">
      <Box className="lease-events-list-title-section">
        <h1 className= {parent === "homePage" ? "home-page-lease-events-list-page-title" : "lease-events-list-page-title"}>
          {parent === "homePage" ? "Upcoming Lease Events" : "Lease Events"}
        </h1>
        {!disableOtherFilter && allLeaseEvents?.lastUpdatedLeaseEvent && (
          <p className="lease-events-list-timestamp">
            Data updated as of {moment.utc(allLeaseEvents.lastUpdatedLeaseEvent).format("DD MMM YYYY")}
          </p>
        )}
      </Box>
      <Box className="lease-events-list-search-section">
        <TextField
          className="lease-events-list-search-input"
          placeholder="Search..."
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              setSearchKeyword(searchValue);
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon className="lease-events-list-search-icon" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {searchValue && (
                  <IconButton
                    edge="end"
                    sx={{
                      padding: '4px',
                      borderRadius: '6px',
                      color: '#6B7280',
                      marginRight: '-8px',
                      '&:hover': {
                        backgroundColor: 'rgba(9, 119, 158, 0.07)',
                        color: '#374151'
                      }
                    }}
                    onClick={() => {
                      setSearchValue("");
                      setSearchKeyword("");
                    }}
                  >
                    <CancelOutlinedIcon fontSize="small" />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
          variant="outlined"
          size="small"
        />
      </Box>
      <Box className="lease-events-list-secondary-buttons">
        <Button
          className="lease-events-list-filter-button"
          variant="text"
          startIcon={
            <Box className="lease-events-list-filter-icon-container">
              <FilterListIcon />
              {getActiveFilterCount() > 0 && (
                <span className="lease-events-list-filter-count-badge">{getActiveFilterCount()}</span>
              )}
            </Box>
          }
          size="small"
          onClick={handleFilterButtonClick}
        >
          <span className="lease-events-list-button-text">Filters</span>
        </Button>
        <Button
          className="lease-events-list-columns-button"
          variant="text"
          startIcon={<ViewColumnOutlinedIcon />}
          size="small"
          onClick={handleColumnButtonClick}
        >
          Columns
        </Button>
        {(
          <>
            <Button
              className="lease-events-list-add-view-button"
              variant="normal"
              startIcon={<AddViewIcon />}
              size="small"
              onClick={handleAddViewClick}
              disabled={!addViewEnabled || disableOtherFilter}
            >
              Add view
            </Button>
            {views.length > 0 && (
              <CustomTooltip
                title={`View: ${headerViewDisplayName}`}
                enterDelay={500}
                leaveDelay={0}
                arrow
                placement="top"
              >
                <Button
                  className="lease-events-list-view-button"
                  variant="normal"
                  size="small"
                  startIcon={<ListIcon />}
                  onClick={handleViewButtonClick}
                  sx={{ maxWidth: 180, minWidth: 0 }}
                >
                  <Box
                    component="span"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      display: "block",
                      minWidth: 0,
                    }}
                  >
                    View: {headerViewDisplayName}
                  </Box>
                </Button>
              </CustomTooltip>
            )}
            {views.length > 0 &&
              !disableOtherFilter &&
              lastAddedViewName &&
              updateViewEnabled &&
              canUpdateCurrentView &&
              appliedViewId && (
                <Button
                  className="lease-events-list-update-view-button"
                  variant="outlined"
                  size="small"
                  startIcon={<AutorenewIcon />}
                  onClick={handleUpdateView}
                  sx={{ height: "28px !important" }}
                >
                  Update View
                </Button>
              )}
          </>
        )}
      </Box>
      <Box className="lease-events-list-spacer" />
      {disableOtherFilter && noticeDate && (
        <Box
          sx={{
            width: "100%",
            flexBasis: "100%",
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
            mt: 0.5,
          }}
        >
          <Chip
            label={`Notice date: ${moment.utc(noticeDate).format("DD MMM YYYY")}`}
            onDelete={handleClearLeaseEventNotificationFilter}
            deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
            variant="outlined"
            size="small"
            sx={{
              backgroundColor: "rgba(30, 105, 118, 0.08)",
              borderColor: "rgba(30, 105, 118, 0.24)",
              color: "#01151D",
              borderRadius: "3px",
              fontWeight: 400,
              "& .MuiChip-label": { px: 1.25, py: 0.5, fontSize: "14px" },
            }}
          />
        </Box>
      )}
       {disableOtherFilter && selectedNotificationCommentId && selectedNotification.type === LeaseEventNotificationComment && (
        <Box
          sx={{
            width: "100%",
            flexBasis: "100%",
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
            mt: 0.5,
          }}
        >
          <CustomTooltip
            title={`Comment: ${selectedNotificationComment}`}
            enterDelay={500}
            leaveDelay={0}
            arrow
            placement="top"
          >
            <Chip
              label={
                <Box
                  component="span"
                  sx={{
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    minWidth: 0,
                  }}
                >
                  {`Comment: ${selectedNotificationComment}`}
                </Box>
              }
              onDelete={handleClearLeaseEventNotificationFilter}
              deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
              variant="outlined"
              size="small"
              sx={{
                maxWidth: 360,
                backgroundColor: "rgba(30, 105, 118, 0.08)",
                borderColor: "rgba(30, 105, 118, 0.24)",
                color: "#01151D",
                borderRadius: "3px",
                fontWeight: 400,
                "& .MuiChip-label": {
                  px: 1.25,
                  py: 0.5,
                  fontSize: "14px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
            />
          </CustomTooltip>
        </Box>
      )}
    </Box>
  );

  const renderLeaseEventsFilterRow = () => (
    <Box className="lease-events-filter-row">
      <Box className="lease-events-filter-row-left">
        <Box className="lease-events-notice-date-container">
          <label className="lease-events-notice-date-label">Notice date</label>
          <TextField
            className="lease-events-notice-date-dropdown"
            select
            value={selectedNoticeDateRange}
            onChange={handleNoticeDateRangeChange}
            disabled={disableOtherFilter}
            size="small"
          >
            {noticeDateRanges.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>
      <Box className="lease-events-filter-row-right">
        <LoadingButton
          className="lease-events-download-button"
          variant="outlined"
          startIcon={<FileDownloadOutlinedIcon />}
          size="small"
          onClick={handleDownloadClick}
          loading={downloadLoading}
          disabled={downloadLoading || loading}
          loadingPosition="start"
        >
          {downloadLoading ? "Downloading..." : "Download"}
        </LoadingButton>
        <ActionButtons
          onPreTransactionClicked={handlePreTransactionClicked}
          onTransactionClicked={handleTransactionClicked}
          onSnoozeClicked={handleSnoozeClicked}
          onUnsnoozeClicked={handleUnsnoozeClicked}
          onIgnoreClicked={handleIgnoreClicked}
          onUnignoreClicked={handleUnignoreClicked}
          eventType={selectedType}
          rowsSelected={selectedIds.length > 0}
          variant="filled"
          className="lease-events-actions-in-filter-row"
        />
      </Box>
    </Box>
  );

  const handleDetailsClosed = () => {
    setDetailsSettings({
      ...detailsSettings,
      open: false,
      selectedLeaseEventId: 0,
    });
  };

  const handleSnoozeIgnoreApply = (settings) => {
    let actionFn = () => {};

    setSnoozeIgnoreModalOpen(false);
    
    switch (snoozeIgnoreAction) {
      case "snooze":
        actionFn = snoozeLeaseEvents;

        break;

      case "ignore":
        actionFn = ignoreLeaseEvents;

        break;

      case "unsnooze":
        actionFn = unSnoozeLeaseEvents;

        break;

      case "unignore":
        actionFn = unIgnoreLeaseEvents;

        break;
    }

    const { userEmailAddress } = user;

    settings.by = userEmailAddress;
    settings.clientId = selectedClientId;

    dispatch(actionFn(settings))
      .then(() => {
        // setAlertMessage(`Successfully ${snoozeIgnoreAction}d lease events`);
        const request = buildRequest();
        request.pageNumber = 1;
        request.pageSize = leaseEventPageSize;
        getEvents(request);
      })
      .catch((error) => {
        setAlertError(
          `Failed to ${snoozeIgnoreAction} lease events.  Error: ${error}`
        );
      });
  };

  const handleTypeTabChange = (newValue) => {
    setSelectedType(newValue);
  };

  const renderSubHeader = () => {
    return (
      <div className="subheader-container">
        <div className="lease-event-types-tabs">
          <Box sx={{ width: "100%" }}>
            <Tabs
              className={`tab-selected-${selectedType}`}
              value={selectedType}
              onChange={(event, newValue) => handleTypeTabChange(newValue)}
            >
              {selectableEventTypes.map((t) => (
                <Tab
                  key={t.name}
                  disableRipple
                  className="lease-events-tab"
                  value={t.name}
                  label={t.label}
                />
              ))}
            </Tabs>
          </Box>
        </div>
      </div>
    );
  };

  const renderDetailsModal = () => {
    const { open, selectedLeaseEventId } = detailsSettings;

    return (
      <DetailsModal
        open={open}
        selectedLeaseEventId={selectedLeaseEventId}
        onClose={handleDetailsClosed}
      />
    );
  };

  const renderSnoozeIgnoreModal = () => {
    let modalLeaseEvents = allLeaseEvents;

    switch (snoozeIgnoreAction) {
      case "unsnooze":
        modalLeaseEvents = leaseEventsSnoozed;
        break;

      case "unignore":
        modalLeaseEvents = leaseEventsIgnored;
        break;
    }

    let selectedLeaseEvents = modalLeaseEvents.leaseEvents.filter(
      ({ leaseEventId }) => selectedIds.includes(leaseEventId)
    );

    return (
      <SnoozeIgnoreModal
        open={snoozeIgnoreModalOpen}
        actionType={snoozeIgnoreAction}
        selectedLeaseEvents={selectedLeaseEvents}
        onApply={handleSnoozeIgnoreApply}
        onClose={() => setSnoozeIgnoreModalOpen(false)}
      />
    );
  };

  const handleSelectionModelChange = (model) => {
    setSelectedIds([...model]);
  };

  const handleAlerted = () => {
    setAlertMessage("");
    setAlertError("");
  };

  const handlePreTransactionClicked = (selectedItems) => {
    dispatch(clearTransactionDetails());
    let url = `/pre-transaction/create?selectedLeaseEventIds=${selectedIds[0]}`;
    let qParms = "";

    selectedIds
      .slice(1)
      .forEach((id) => (qParms += `&selectedLeaseEventIds=${id}`));

    url += qParms;

    window.pendo.track(
      `${window.jllConfig.env} | New Pre-Transaction Initialize`,
      {}
    );
    history.push(url, {from : "lease-event"});
  };

  const handleTransactionClicked = () => {
    dispatch(clearTransactionDetails());
    let url = `/transaction/create?selectedLeaseEventIds=${selectedIds[0]}`;
    let qParms = "";

    selectedIds
      .slice(1)
      .forEach((id) => (qParms += `&selectedLeaseEventIds=${id}`));

    url += qParms;

    window.pendo.track(
      `${window.jllConfig.env} | New Transaction Initialize`,
      {}
    );

    history.push(url, {from : "lease-event"});
  };

  const handleSnoozeClicked = () => {
    setSnoozeIgnoreAction("snooze");
    setSnoozeIgnoreModalOpen(true);
  };

  const handleUnsnoozeClicked = () => {
    setSnoozeIgnoreAction("unsnooze");
    setSnoozeIgnoreModalOpen(true);
  };

  const handleIgnoreClicked = () => {
    setSnoozeIgnoreAction("ignore");
    setSnoozeIgnoreModalOpen(true);
  };

  const handleUnignoreClicked = () => {
    setSnoozeIgnoreAction("unignore");
    setSnoozeIgnoreModalOpen(true);
  };

  const handleTabChange = (tabIdx) => {
    switch (tabIdx) {
      case 0:
        setSelectedType("all");
        break;
      case 1:
        setSelectedType("snoozed");
        break;
      case 2:
        setSelectedType("ignored");
        break;
    }
  };

  const handleRefresh = () => {
    let request = buildRequest();

    getEvents(request);
  };

  const renderTabSection = (
    tabIdx,
    data,
    eventType,
    columnVisibilityObj,
    user
  ) => {
    const { totalLeaseEvents, leaseEvents } = data;
    return (
      <div className="tab-section">
        <ActionButtons
          onPreTransactionClicked={handlePreTransactionClicked}
          onTransactionClicked={handleTransactionClicked}
          onSnoozeClicked={handleSnoozeClicked}
          onUnsnoozeClicked={handleUnsnoozeClicked}
          onIgnoreClicked={handleIgnoreClicked}
          onUnignoreClicked={handleUnignoreClicked}
          eventType={selectedType}
          rowsSelected={selectedIds.length > 0}
        />
        <LeaseEventsTable
          loading={loading}
          isAdmin={user.isAdmin}
          eventType={eventType}
          request={buildRequest}
          leaseEvents={leaseEvents}
          totalLeaseEvents={totalLeaseEvents}
          onLeaseNameClicked={handleLeaseNameClicked}
          onPageChange={(page) => handlePageChange(page, tabIdx)}
          onSortModelChange={(model) => handleSortModelChange(model, tabIdx)}
          onRefresh={handleRefresh}
          rowSelectionModel={selectedIds}
          onRowSelectionModelChange={(model) =>
            handleSelectionModelChange(model, tabIdx)
          }
          columnVisibilityModel={columnVisibilityObj}
          columnVisibilityModelChange={(newModel) =>
            handleColumnVisibilityModelChange(newModel, eventType)
          }
          columnOrderSettings={columnOrderSettings}
          columnOrderSettingsChange={handleColumnOrderChange}
          actionIcons={["comment"]}
          pageSize={leaseEventPageSize}
        />
      </div>
    );
  };

  let leaseEventsMeta = {};

  switch (selectedType) {
    case "all":
    default:
      leaseEventsMeta = allLeaseEvents;
      break;

    case "snoozed":
      leaseEventsMeta = leaseEventsSnoozed;
      break;

    case "ignored":
      leaseEventsMeta = leaseEventsIgnored;
  }

  const { totalLeaseEvents, leaseEvents } = leaseEventsMeta;
  const previousLoading = usePrevious(loading);

  useEffect(() => {
    if (
      previousLoading &&
      !loading &&
      disableOtherFilter &&
      selectedNotification.type === LeaseEventNotificationComment &&
      selectedNotificationCommentId &&
      Number(totalLeaseEvents) === 0
    ) {
      setNotificationAlert({
        message:
          "The comment has been deleted, or you have been removed from the comment.",
        severity: "error",
        title: "Comment is no longer available.",
        open: true,
      });
    }
  }, [
    previousLoading,
    loading,
    disableOtherFilter,
    selectedNotificationCommentId,
    totalLeaseEvents,
  ]);

  const onNotificationAlertClose = () => {
    setNotificationAlert({
      message: "",
      severity: "",
      title: "",
      open: false,
    });
    if (selectedNotification && selectedNotification.type === LeaseEventNotificationComment) {
      dispatch(userActions.resetSelectedNotificationComment());
    }
  };

  return (
    <Container>
      <NotificationAlert
        onClose={onNotificationAlertClose}
        message={notificationAlert.message}
        open={notificationAlert.open}
        title={notificationAlert.title}
        severity={notificationAlert.severity}
      />
      <Alert
        message={alertMessage}
        error={alertError}
        onAlerted={handleAlerted}
      />
      {renderDetailsModal()}
      {renderSnoozeIgnoreModal(snoozeIgnoreAction)}
      <Row style={{ marginTop: "0px"}}>
        {
          renderLeaseEventsHeader() 
        }
      </Row>     
        <Row>
          <FitWidth>{renderSubHeader()}</FitWidth>
        </Row>
        <Row>
          <FitWidth>{renderLeaseEventsFilterRow()}</FitWidth>
        </Row>
     
      {(
        <>
          <Row>
            <FitWidth>
              <LeaseEventsTable
                loading={loading}
                isAdmin={user.isAdmin}
                leaseEvents={leaseEvents}
                eventType={selectedType}
                request={buildRequest}
                totalLeaseEvents={totalLeaseEvents}
                onLeaseNameClicked={handleLeaseNameClicked}
                onPageChange={handlePageChange}
                onSortModelChange={handleSortModelChange}
                onRefresh={handleRefresh}
                selectionModel={selectedIds}
                onRowSelectionModelChange={handleSelectionModelChange}
                columnVisibilityModel={columnVisibilityByType}
                columnVisibilityModelChange={(newModel) =>
                  handleColumnVisibilityByTypeChange(newModel, selectedType)
                }
                columnOrderSettings={columnOrderSettings}
                columnOrderSettingsChange={handleColumnOrderChange}
                actionIcons={["comment"]}
                pageSize={leaseEventPageSize}
                hideToolbar
                parent={parent}
              />
            </FitWidth>
          </Row>
        </>
      )}
      {(
        <>
          <CreateViewModal
            open={addViewDialogOpen}
            onClose={handleAddViewDialogClose}
            onSave={handleAddViewSave}
            existingViews={views}
            initialSetAsDefault={!hasOwnedViews}
          />
          <ViewListDrawer
            open={viewDrawerOpen}
            onClose={handleCloseViewDrawer}
            tableName={savedFilterTableName}
            userId={userId}
            clientId={selectedClientId}
            currentUserEmail={user?.userEmailAddress || ""}
            selectedViewId={viewDrawerSelectedId}
            onSelectView={handleSelectView}
            onSetDefault={handleSetDefaultView}
            onRenameView={handleRenameViewClick}
            onShareToggle={handleShareToggleView}
            onDeleteView={handleDeleteViewClick}
          />
          <ViewActionModal
            open={!!viewToDelete}
            onClose={handleCancelDeleteView}
            onConfirm={handleConfirmDeleteView}
            title={viewToDelete?.isPrivate ? "Delete custom view" : "Delete public view"}
            message={
              viewToDelete?.isPrivate ? (
                <>
                  Are you sure you want to delete the view?
                  <br />
                  Once the view is deleted, it cannot be recovered.
                </>
              ) : (
                "Are you sure you want to delete the view for everyone in your account? Once the view is deleted, it cannot be recovered."
              )
            }
            cancelButtonText="Cancel"
            confirmButtonText="Delete"
            confirmButtonColor="error"
          />
          <ViewActionModal
            open={!!viewToShare}
            onClose={handleCancelShareView}
            onConfirm={handleConfirmShareView}
            title="Share with others"
            message="Are you sure you want to share the view with everyone in your account?"
            showCheckbox={true}
            checkboxLabel="Restricted"
            checkboxValue={shareRestricted}
            onCheckboxChange={setShareRestricted}
            showCheckboxTooltip={true}
            checkboxTooltipText="If restricted, only you can modify this view. If unrestricted, anyone with view access can edit the view."
            cancelButtonText="Cancel"
            confirmButtonText="Share"
            confirmButtonColor="primary"
          />
          <ViewActionModal
            open={!!viewToMakePrivate}
            onClose={handleCancelMakePrivate}
            onConfirm={handleConfirmMakePrivate}
            title="Make this view private"
            message="Making this view private will remove access for all other users in your account. Only you will be able to use this view."
            cancelButtonText="Cancel"
            confirmButtonText="Make private"
            confirmButtonColor="primary"
          />
          <ViewActionModal
            open={!!viewToRestrict}
            onClose={handleCancelRestrict}
            onConfirm={handleConfirmRestrict}
            title="Restrict this view"
            message="Are you sure you want to restrict this view? If restricted, only you can modify this view."
            cancelButtonText="Cancel"
            confirmButtonText="Restrict"
            confirmButtonColor="primary"
          />
          <ViewActionModal
            open={!!viewToUnrestrict}
            onClose={handleCancelUnrestrict}
            onConfirm={handleConfirmUnrestrict}
            title="Unrestrict this view"
            message="Are you sure you want to allow everyone in your account to modify this view?"
            cancelButtonText="Cancel"
            confirmButtonText="Unrestrict"
            confirmButtonColor="primary"
          />
          <SuccessAlert
            open={successAlertOpen}
            message={successAlertMessage}
            onClose={handleSuccessAlertClose}
            duration={3000}
          />
          <FilterModal
            open={filterModalOpen}
            onClose={handleFilterModalClose}
            filters={filters.filter((f) => f.name !== "noticeDateRange")}
            selectedFilters={effectiveSelectedFilters}
            onApplyFilter={handleFilterModalApply}
            onClearFilter={handleFilterModalClear}
          />
          <ColumnVisibilityModal
            open={columnModalOpen}
            onClose={handleColumnModalClose}
            defaultColumnVisibilityModel={defaultColumnVisibility}
            columnSettings={columnOrderSettings}
            onColumnVisibilityChange={(changes) =>
              handleColumnVisibilityByTypeChange(changes, selectedType)
            }
            onColumnOrderChange={handleColumnOrderChange}
            onReset={handleColumnReset}
            excludeColumns={["actions", "lastCommentCreatedDate"]}
          />
        </>
      )}
    </Container>
  );
};

export default LeaseEvents;
